import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from './store.models';
import { DeliveryMessage, OrderStatus, ReorderResult } from './order.models';
import { API_BASE } from './ordering-api';

/** Reads the signed-in customer's orders (token attached by customerAuthInterceptor). */
@Injectable({ providedIn: 'root' })
export class OrderService {
  private readonly http = inject(HttpClient);

  track(orderId: string): Observable<ApiResponse<OrderStatus>> {
    return this.http.get<ApiResponse<OrderStatus>>(`${API_BASE}/api/public/orders/${orderId}`);
  }

  myOrders(): Observable<ApiResponse<OrderStatus[]>> {
    return this.http.get<ApiResponse<OrderStatus[]>>(`${API_BASE}/api/public/orders`);
  }

  /** Store + line snapshot of a past order, for repopulating the cart on reorder. */
  reorder(orderId: string): Observable<ApiResponse<ReorderResult>> {
    return this.http.get<ApiResponse<ReorderResult>>(`${API_BASE}/api/public/orders/${orderId}/reorder`);
  }

  /** Rate the rider for a delivered order (1–5 stars + optional comment). */
  rateRider(orderId: string, rating: number, comment: string | null): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API_BASE}/api/public/orders/${orderId}/rate-rider`, { rating, comment });
  }

  /**
   * "I'm coming out" — tell the rider we're walking down (with the cash, if it's cash on delivery).
   * The server re-checks that the rider is actually nearby, so a 400 here is a legitimate "not yet",
   * not a bug: the rider may have moved away between the poll that showed the button and this tap.
   */
  headingOut(orderId: string): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${API_BASE}/api/public/orders/${orderId}/heading-out`, {});
  }

  /** Rider↔customer chat for the order. */
  messages(orderId: string): Observable<ApiResponse<DeliveryMessage[]>> {
    return this.http.get<ApiResponse<DeliveryMessage[]>>(`${API_BASE}/api/public/orders/${orderId}/messages`);
  }
  sendMessage(orderId: string, text: string): Observable<ApiResponse<DeliveryMessage>> {
    return this.http.post<ApiResponse<DeliveryMessage>>(`${API_BASE}/api/public/orders/${orderId}/messages`, { text });
  }

  /**
   * Customer↔store chat — a SEPARATE thread from the rider one above. The store never sees the rider
   * conversation and vice versa, which is why these are distinct endpoints rather than a channel flag.
   */
  storeMessages(orderId: string): Observable<ApiResponse<DeliveryMessage[]>> {
    return this.http.get<ApiResponse<DeliveryMessage[]>>(`${API_BASE}/api/public/orders/${orderId}/store-messages`);
  }
  sendStoreMessage(orderId: string, text: string): Observable<ApiResponse<DeliveryMessage>> {
    return this.http.post<ApiResponse<DeliveryMessage>>(`${API_BASE}/api/public/orders/${orderId}/store-messages`, { text });
  }
}
